import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Users2,
  Plus,
  MessageCircle,
  Wallet,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Pencil,
  Save,
  X,
  Send,
  History,
  Info,
} from "lucide-react";

/**
 * ReferralSection · Programa de Referidos
 *
 * Renders inside the student ficha (StudentSummaryDialog). 1 alumno = 1
 * registro. Flow: sin_gestionar → pendiente_contacto → pago_pendiente → pagado.
 *
 * Backend routes:
 *   GET    /api/students/{id}/referral
 *   POST   /api/students/{id}/referral                       (create)
 *   PATCH  /api/students/{id}/referral                       (editable fields)
 *   POST   /api/students/{id}/referral/mark-sent             (fase 1 WA)
 *   POST   /api/students/{id}/referral/bizum-confirmed       (fase 2)
 *   POST   /api/students/{id}/referral/register-payment      (fase 3)
 *   POST   /api/students/{id}/referral/mark-paid-notified    (post-pago WA)
 */

const STATUS_META = {
  sin_gestionar:       { label: "Sin gestionar",              cls: "bg-neutral-100 text-neutral-700 border-neutral-300" },
  pendiente_contacto:  { label: "Pendiente de contacto",      cls: "bg-amber-100 text-amber-800 border-amber-300" },
  pendiente_bizum:     { label: "Pendiente conf. Bizum",       cls: "bg-orange-100 text-orange-800 border-orange-300" },
  pago_pendiente:      { label: "Pago pendiente",              cls: "bg-red-100 text-red-800 border-red-300" },
  pagado:              { label: "Pagado",                      cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const REFERENT_TYPE_LABEL = {
  alumno: "Alumno Natur",
  exalumno: "Exalumno Natur",
  externo: "Persona externa",
};

const buildWhatsAppLink = (phone, text) => {
  const clean = String(phone || "").replace(/\D+/g, "");
  const withCC = clean.startsWith("34") ? clean : (clean.length === 9 ? "34" + clean : clean);
  return `https://wa.me/${withCC}?text=${encodeURIComponent(text)}`;
};


// ------- Create form (used when no referral yet) -------
function CreateReferralForm({ studentId, allStudents, onCreated, onCancel }) {
  const [refType, setRefType] = useState("alumno");
  const [refStudentId, setRefStudentId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("30");
  const [observations, setObservations] = useState("");
  const [saving, setSaving] = useState(false);

  const eligibleStudents = useMemo(
    () => (allStudents || []).filter((s) => s.id !== studentId),
    [allStudents, studentId],
  );

  // When user picks an existing student, prefill name+phone ONCE (still editable).
  const onPickStudent = (id) => {
    setRefStudentId(id);
    const s = eligibleStudents.find((x) => x.id === id);
    if (s) {
      const fullName = [s.name, s.primer_apellido].filter(Boolean).join(" ").trim();
      setName(fullName);
      setPhone(s.phone || "");
    }
  };

  const submit = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    if (!(parseFloat(amount) >= 0)) {
      toast.error("Importe inválido");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/students/${studentId}/referral`, {
        referent_type: refType,
        referent_student_id: refType === "externo" ? "" : refStudentId,
        referent_name: name.trim(),
        referent_phone: phone.trim(),
        amount: parseFloat(amount),
        observations: observations.trim(),
      });
      toast.success("Referido creado");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo crear el referido"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3" data-testid="referral-create-form">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm">Añadir referido</h4>
        <button
          onClick={onCancel}
          className="text-xs text-neutral-500 hover:text-neutral-800"
          data-testid="referral-create-cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[11px] font-semibold text-neutral-600">Tipo de referente</span>
          <select
            value={refType}
            onChange={(e) => { setRefType(e.target.value); if (e.target.value === "externo") setRefStudentId(""); }}
            data-testid="referral-referent-type"
            className="mt-1 w-full h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm"
          >
            <option value="alumno">Alumno Natur</option>
            <option value="exalumno">Exalumno Natur</option>
            <option value="externo">Persona externa</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] font-semibold text-neutral-600">Importe (€)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="referral-amount"
            className="mt-1 w-full h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm"
          />
        </label>
        {refType !== "externo" && (
          <label className="block sm:col-span-2">
            <span className="text-[11px] font-semibold text-neutral-600">Selecciona alumno referente</span>
            <select
              value={refStudentId}
              onChange={(e) => onPickStudent(e.target.value)}
              data-testid="referral-referent-picker"
              className="mt-1 w-full h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm"
            >
              <option value="">— Elegir —</option>
              {eligibleStudents.map((s) => (
                <option key={s.id} value={s.id}>
                  {[s.name, s.primer_apellido].filter(Boolean).join(" ")} · {s.phone || "sin tlf"}
                </option>
              ))}
            </select>
            <span className="text-[10px] text-neutral-500 mt-1 block">
              Al elegir, se copiarán nombre y teléfono (editables abajo). No se enlazan dinámicamente.
            </span>
          </label>
        )}
        <label className="block">
          <span className="text-[11px] font-semibold text-neutral-600">Nombre referente *</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="referral-name"
            className="mt-1 w-full h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm"
          />
        </label>
        <label className="block">
          <span className="text-[11px] font-semibold text-neutral-600">Teléfono referente *</span>
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="referral-phone"
            className="mt-1 w-full h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[11px] font-semibold text-neutral-600">Observaciones internas</span>
          <textarea
            rows={2}
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            data-testid="referral-observations"
            className="mt-1 w-full p-2 rounded-lg border border-neutral-300 bg-white text-sm resize-none"
          />
        </label>
      </div>
      <div className="flex justify-end">
        <button
          onClick={submit}
          disabled={saving}
          data-testid="referral-create-submit"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Crear referido
        </button>
      </div>
    </div>
  );
}


// ------- Compact card once a referral exists -------
function ReferralCard({ referral, studentName, onChanged, autoescuelaName }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const isPaid = referral.status === "pagado";
  const st = STATUS_META[referral.status] || STATUS_META.sin_gestionar;

  const [name, setName] = useState(referral.referent_name || "");
  const [phone, setPhone] = useState(referral.referent_phone || "");
  const [amount, setAmount] = useState(String(referral.amount ?? 0));
  const [observations, setObservations] = useState(referral.observations || "");

  useEffect(() => {
    setName(referral.referent_name || "");
    setPhone(referral.referent_phone || "");
    setAmount(String(referral.amount ?? 0));
    setObservations(referral.observations || "");
  }, [referral]);

  const savePatch = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/students/${referral.student_id}/referral`, {
        referent_name: name.trim(),
        referent_phone: phone.trim(),
        amount: parseFloat(amount || 0),
        observations,
      });
      toast.success("Guardado");
      setEditing(false);
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo guardar"));
    } finally {
      setSaving(false);
    }
  };

  const sendWA = async () => {
    if (!phone) { toast.error("Falta teléfono del referente"); return; }
    const msg =
      `Buenos días 👋\n\nDesde el Programa de Referidos de ${autoescuelaName || "Natur Autoescuela"} tienes disponible un incentivo de ${referral.amount}€.\n\n` +
      `Te lo enviaremos mediante Bizum.\n\n¿Puedes confirmarme si deseas recibirlo en este número: ${phone}?\n\n` +
      `Muchas gracias por tu apoyo 💪🏻🚗✨`;
    const wa = buildWhatsAppLink(phone, msg);
    try {
      await api.post(`/students/${referral.student_id}/referral/mark-sent`, { channel: "whatsapp" });
      window.open(wa, "_blank", "noopener,noreferrer");
      const { data } = await api.get(`/students/${referral.student_id}/referral`);
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo marcar como enviado"));
    }
  };

  const confirmBizum = async () => {
    const bizumNumber = window.prompt(
      "Número Bizum confirmado por el referente (teléfono destino):",
      phone,
    );
    if (!bizumNumber || !bizumNumber.trim()) return;
    const fecha = window.prompt(
      "Fecha de la confirmación (YYYY-MM-DD, deja vacío para hoy):",
      "",
    );
    try {
      const { data } = await api.post(`/students/${referral.student_id}/referral/bizum-confirmed`, {
        bizum_number: bizumNumber.trim(),
        fecha_confirmacion: (fecha || "").trim(),
      });
      toast.success("Bizum confirmado. Pago pendiente.");
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo confirmar el Bizum"));
    }
  };

  const registerPayment = async () => {
    const amtRaw = window.prompt(
      `Importe pagado (€) — importe ficha: ${referral.amount}:`,
      String(referral.amount || 0),
    );
    if (amtRaw === null) return;
    const amt = parseFloat(amtRaw);
    if (!(amt >= 0)) { toast.error("Importe inválido"); return; }
    const fecha = window.prompt("Fecha del pago (YYYY-MM-DD, deja vacío para hoy):", "");
    const obs = window.prompt("Observaciones del pago (opcional):", "") || "";
    try {
      const { data } = await api.post(`/students/${referral.student_id}/referral/register-payment`, {
        amount_paid: amt,
        fecha_pago: (fecha || "").trim(),
        observations: obs.trim(),
      });
      toast.success("Pago registrado ✓");
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo registrar el pago"));
    }
  };

  const sendPaidWA = async () => {
    const msg =
      `Hola 👋\n\nTe confirmamos que el pago de tu incentivo del Programa de Referidos de ${autoescuelaName || "Natur Autoescuela"} ha sido realizado correctamente.\n\n` +
      `Gracias por confiar en nosotros y ayudarnos a seguir creciendo 🚗💥✨💨\n\n¡Nos vemos pronto!`;
    const wa = buildWhatsAppLink(phone, msg);
    try {
      await api.post(`/students/${referral.student_id}/referral/mark-paid-notified`);
      window.open(wa, "_blank", "noopener,noreferrer");
      const { data } = await api.get(`/students/${referral.student_id}/referral`);
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo enviar el aviso"));
    }
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3" data-testid="referral-card">
      {referral.is_alerting && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800" data-testid="referral-alert-banner">
          <AlertTriangle className="h-4 w-4" />
          🔔 Referido pendiente de gestionar — más de {referral.days_in_status} días en «{st.label}».
        </div>
      )}

      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-orange-600 mb-1 flex items-center gap-1">
            <Users2 className="h-3 w-3" /> Programa de Referidos
          </div>
          <div className="text-lg font-bold text-neutral-900" data-testid="referral-status-label">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold border ${st.cls}`}>
              {st.label}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase text-neutral-500 font-bold">Importe</div>
          <div className="font-mono text-xl font-bold text-neutral-900" data-testid="referral-amount-display">
            {(isPaid ? referral.amount_paid : referral.amount)?.toFixed(2)} €
          </div>
          {isPaid && referral.amount_paid !== referral.amount && (
            <div className="text-[10px] text-neutral-400">Ficha: {referral.amount?.toFixed(2)} €</div>
          )}
        </div>
      </div>

      {!editing ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Referido por</div>
            <div className="font-semibold" data-testid="referral-referent-name">{referral.referent_name || "—"}</div>
            <div className="text-xs text-neutral-500">{referral.referent_phone || "—"}</div>
            <div className="text-[10px] text-neutral-400 mt-1">{REFERENT_TYPE_LABEL[referral.referent_type] || referral.referent_type}</div>
          </div>
          <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2 space-y-1 text-xs">
            {referral.fecha_generacion && <div>📤 Enviado: <b>{referral.fecha_generacion.slice(0, 10)}</b></div>}
            {referral.fecha_confirmacion && <div>✅ Bizum: <b>{referral.fecha_confirmacion.slice(0, 10)}</b> — <span className="font-mono">{referral.bizum_number}</span></div>}
            {referral.fecha_pago && <div>💸 Pagado: <b>{referral.fecha_pago.slice(0, 10)}</b> por {referral.paid_by}</div>}
            {referral.observations && (
              <div className="pt-1 border-t border-neutral-200 mt-1">
                <span className="text-neutral-500">Obs:</span> {referral.observations}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="block">
            <span className="text-[11px] font-semibold text-neutral-600">Nombre referente</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm" data-testid="referral-edit-name" />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold text-neutral-600">Teléfono referente</span>
            <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm" data-testid="referral-edit-phone" />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold text-neutral-600">Importe (€)</span>
            <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-full h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm" data-testid="referral-edit-amount" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[11px] font-semibold text-neutral-600">Observaciones</span>
            <textarea rows={2} value={observations} onChange={(e) => setObservations(e.target.value)} className="mt-1 w-full p-2 rounded-lg border border-neutral-300 bg-white text-sm resize-none" data-testid="referral-edit-observations" />
          </label>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-neutral-100">
        {editing ? (
          <>
            <button onClick={savePatch} disabled={saving} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50" data-testid="referral-edit-save">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Guardar
            </button>
            <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-neutral-300 text-xs font-semibold text-neutral-700 hover:bg-neutral-50" data-testid="referral-edit-cancel">
              <X className="h-3 w-3" /> Cancelar
            </button>
          </>
        ) : (
          <>
            {!isPaid && (
              <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg border border-neutral-300 text-xs font-semibold text-neutral-700 hover:bg-neutral-50" data-testid="referral-edit-btn">
                <Pencil className="h-3 w-3" /> Editar datos
              </button>
            )}
            {(referral.status === "sin_gestionar" || referral.status === "pendiente_contacto") && (
              <button onClick={sendWA} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold" data-testid="referral-wa-send">
                <MessageCircle className="h-3 w-3" /> Enviar WhatsApp Referido
              </button>
            )}
            {(referral.status === "sin_gestionar" || referral.status === "pendiente_contacto" || referral.status === "pendiente_bizum") && (
              <button onClick={confirmBizum} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold" data-testid="referral-bizum-confirmed">
                <ShieldCheck className="h-3 w-3" /> Bizum confirmado
              </button>
            )}
            {referral.status === "pago_pendiente" && (
              <button onClick={registerPayment} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold animate-pulse" data-testid="referral-register-payment">
                <Wallet className="h-3 w-3" /> Registrar pago realizado
              </button>
            )}
            {isPaid && (
              <button onClick={sendPaidWA} className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold" data-testid="referral-wa-paid">
                <Send className="h-3 w-3" /> {referral.wa_paid_sent ? "Reenviar" : "Enviar"} WhatsApp Pago Realizado
              </button>
            )}
          </>
        )}
      </div>

      {/* Audit trail */}
      {referral.audit && referral.audit.length > 0 && (
        <details className="text-[11px] text-neutral-500">
          <summary className="cursor-pointer inline-flex items-center gap-1 font-semibold hover:text-neutral-800">
            <History className="h-3 w-3" /> Trazabilidad ({referral.audit.length})
          </summary>
          <ul className="mt-1 space-y-0.5 font-mono pl-4">
            {referral.audit.slice().reverse().map((a, i) => (
              <li key={i}>
                <span className="text-neutral-400">{a.at?.slice(0, 19).replace("T", " ")}</span> · <b>{a.event}</b> · {a.by}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


// ------- Main section -------
export default function ReferralSection({ studentId, studentName }) {
  const [loading, setLoading] = useState(true);
  const [referral, setReferral] = useState(null);
  const [creating, setCreating] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [autoescuelaName, setAutoescuelaName] = useState("Natur Autoescuela");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/referral`);
      setReferral(data.exists ? data : null);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  // Lazy load ancillary data only when the create form is opened
  useEffect(() => {
    if (!creating || allStudents.length > 0) return;
    (async () => {
      try {
        const [{ data: ss }, { data: nm }] = await Promise.all([
          api.get("/students"),
          api.get("/settings/autoescuela_nombre").catch(() => ({ data: {} })),
        ]);
        setAllStudents(Array.isArray(ss) ? ss : ss?.items || []);
        if (nm?.value) setAutoescuelaName(nm.value);
      } catch { /* silent */ }
    })();
  }, [creating, allStudents.length]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-neutral-500 py-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando referido…
      </div>
    );
  }

  if (!referral && !creating) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-4 text-center" data-testid="referral-empty">
        <Users2 className="h-6 w-6 text-orange-500 mx-auto mb-1" />
        <h4 className="font-semibold text-sm">Programa de Referidos</h4>
        <p className="text-xs text-neutral-500 mb-3">
          Este alumno no tiene referente registrado.
        </p>
        <button
          onClick={() => setCreating(true)}
          data-testid="referral-add-btn"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold"
        >
          <Plus className="h-4 w-4" /> Añadir referido
        </button>
      </div>
    );
  }

  if (creating && !referral) {
    return (
      <CreateReferralForm
        studentId={studentId}
        allStudents={allStudents}
        onCancel={() => setCreating(false)}
        onCreated={(data) => { setReferral(data); setCreating(false); }}
      />
    );
  }

  return (
    <ReferralCard
      referral={referral}
      studentName={studentName}
      autoescuelaName={autoescuelaName}
      onChanged={(data) => setReferral(data)}
    />
  );
}
